import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Client } from '../models/Client';

export function useClient(clientId: string | null) {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!clientId) {
      setClient(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let unsubscribe: (() => void) | null = null;
    const subscribe = (collectionName: 'projects' | 'clients') => {
      const projectRef = doc(db, collectionName, clientId);

      unsubscribe = onSnapshot(
        projectRef,
        async (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            setClient({
              id: snapshot.id,
              ...data,
              entityType: data.entityType || 'project',
              clientId: data.clientId || data.organizationId || '',
              organizationId: data.organizationId || data.clientId || '',
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Client);
            setLoading(false);
            setError(null);
            return;
          }

          if (collectionName === 'projects') {
            const legacySnap = await getDoc(doc(db, 'clients', clientId));
            if (legacySnap.exists()) {
              if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
              }
              subscribe('clients');
              return;
            }
          }

          setClient(null);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching project/client:', err);
          setError(err as Error);
          setLoading(false);
        }
      );
    };

    subscribe('projects');

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [clientId]);

  const updateClient = async (updates: Partial<Client>) => {
    if (!clientId) return;
    
    try {
      const clientRef = doc(db, 'projects', clientId);
      await updateDoc(clientRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating client:', err);
      throw err;
    }
  };

  return {
    client,
    loading,
    error,
    updateClient,
  };
}
